import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import Footer from "../footer/Footer";
import Scrollbar from "../scrollbar/scrollbar";

const ALL_POLICIES = [
  { slug: "privacy", to: "/privacy", label: "Privacy Policy",
    blurb: "How we collect, use and protect your data." },
  { slug: "tos", to: "/tos", label: "Terms of Service",
    blurb: "The agreement between you and GlobalGiftPass." },
  { slug: "rr", to: "/rr", label: "Refund & Return",
    blurb: "When refunds apply and how they're processed." },
  { slug: "shipping", to: "/shipping", label: "Usage Policy",
    blurb: "Rules for using vouchers, gift cards and accounts." },
];

/**
 * sections = [{ id: 'foo', title: 'Foo', body: <>...</> | string }, ...]
 */
const PolicyLayout = ({
  eyebrow,
  title,
  accent,
  sub,
  lastUpdated,
  intro,
  sections = [],
  slug,
  notice,
}) => {
  const [activeId, setActiveId] = useState(sections[0]?.id || null);

  const otherPolicies = useMemo(
    () => ALL_POLICIES.filter((p) => p.slug !== slug),
    [slug]
  );

  // Active section tracking for the table of contents
  useEffect(() => {
    if (typeof window === "undefined" || !sections.length) return;

    const onScroll = () => {
      const offset = 160;
      let current = sections[0].id;
      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top - offset <= 0) current = s.id;
      }
      setActiveId(current);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [sections]);

  const handleAnchor = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 110;
    window.scrollTo({ top, behavior: "smooth" });
    setActiveId(id);
  };

  const renderBody = (body) => {
    if (body == null) return null;
    if (Array.isArray(body)) {
      return body.map((p, i) =>
        typeof p === "string" ? <p key={i}>{p}</p> : <Fragment key={i}>{p}</Fragment>
      );
    }
    if (typeof body === "string") return <p>{body}</p>;
    return body;
  };

  return (
    <Fragment>
      <Navbar />

      <section className="gppl-section">
        <div className="gppl-bg-orb gppl-bg-orb-tl" />
        <div className="gppl-bg-orb gppl-bg-orb-br" />

        <div className="gppl-inner">
          {/* HERO */}
          <div className="gppl-head">
            <div className="gppl-eyebrow">
              <span className="gppl-eyebrow-dot" />
              {eyebrow}
            </div>
            <h1 className="gppl-title">
              {title} <span className="gppl-title-accent">{accent}</span>
            </h1>
            {sub && <p className="gppl-sub">{sub}</p>}
            {lastUpdated && (
              <div className="gppl-last-updated">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                Last updated: <strong>{lastUpdated}</strong>
              </div>
            )}
          </div>

          <div className="gppl-grid">
            {/* TABLE OF CONTENTS */}
            <aside className="gppl-toc-wrap">
              <div className="gppl-toc">
                <div className="gppl-toc-label">ON THIS PAGE</div>
                <nav className="gppl-toc-list" aria-label="Table of contents">
                  {sections.map((s) => (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      onClick={(e) => handleAnchor(e, s.id)}
                      className={`gppl-toc-link ${activeId === s.id ? "is-active" : ""}`}
                    >
                      <span className="gppl-toc-dot" />
                      {s.title}
                    </a>
                  ))}
                </nav>
              </div>

              <div className="gppl-other">
                <div className="gppl-toc-label">OTHER POLICIES</div>
                <div className="gppl-other-list">
                  {otherPolicies.map((p) => (
                    <Link key={p.slug} to={p.to} className="gppl-other-link">
                      <div>
                        <strong>{p.label}</strong>
                        <small>{p.blurb}</small>
                      </div>
                      <span className="gppl-other-arrow">→</span>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>

            {/* CONTENT */}
            <article className="gppl-content">
              {intro && (
                <div className="gppl-intro">
                  {typeof intro === "string" ? <p>{intro}</p> : intro}
                </div>
              )}

              {notice && (
                <div className="gppl-notice">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12 2L2 7v7c0 5.55 3.84 10.74 10 12 6.16-1.26 10-6.45 10-12V7z" />
                  </svg>
                  <span>{notice}</span>
                </div>
              )}

              {sections.map((s, i) => (
                <section
                  key={s.id}
                  id={s.id}
                  className="gppl-content-section"
                  aria-labelledby={`${s.id}-h`}
                >
                  <div className="gppl-section-num">{String(i + 1).padStart(2, "0")}</div>
                  <h2 id={`${s.id}-h`} className="gppl-section-title">
                    {s.title}
                  </h2>
                  <div className="gppl-section-body">{renderBody(s.body)}</div>
                </section>
              ))}

              {/* Closing card */}
              <div className="gppl-contact-card">
                <div className="gppl-contact-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5z" />
                  </svg>
                </div>
                <h3>Questions about this policy?</h3>
                <p>
                  We're happy to clarify anything — reach us on WhatsApp or
                  send an email and we'll get back to you.
                </p>
                <div className="gppl-contact-actions">
                  <a href="mailto:support@globalgiftpass.io" className="gppl-btn gppl-btn-primary">
                    Email Support
                  </a>
                  <Link to="/support" className="gppl-btn gppl-btn-ghost">
                    Contact Form
                  </Link>
                </div>
                <div className="gppl-contact-meta">
                  VentureWorks Dynamics LLC · Sharjah Media City, Sharjah, UAE
                </div>
              </div>
            </article>
          </div>
        </div>

        <style>{`
          .gppl-section {
            position: relative;
            background: linear-gradient(180deg, #07060a 0%, #0b0b0c 100%);
            color: #e6e8ec;
            padding: 60px 22px 90px;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            overflow: hidden;
          }
          .gppl-bg-orb {
            position: absolute;
            width: 460px; height: 460px;
            border-radius: 50%;
            filter: blur(140px);
            opacity: 0.4;
            pointer-events: none;
          }
          .gppl-bg-orb-tl { background: rgba(212,168,87,0.18); top: -180px; left: -120px; }
          .gppl-bg-orb-br { background: rgba(212,168,87,0.12); bottom: -200px; right: -120px; }

          .gppl-inner { position: relative; max-width: 1200px; margin: 0 auto; z-index: 1; }

          /* HERO */
          .gppl-head { text-align: center; margin-bottom: 38px; }
          .gppl-eyebrow {
            display: inline-flex; align-items: center; gap: 8px;
            padding: 6px 14px;
            background: rgba(212,168,87,0.1);
            border: 1px solid rgba(212,168,87,0.3);
            color: #d4a857;
            border-radius: 999px;
            font-size: 11px; font-weight: 700; letter-spacing: 2px;
            margin-bottom: 14px;
          }
          .gppl-eyebrow-dot {
            width: 6px; height: 6px; border-radius: 50%;
            background: #d4a857;
            box-shadow: 0 0 10px #d4a857;
            animation: gppl-pulse 1.6s ease-in-out infinite;
          }
          @keyframes gppl-pulse { 0%,100% { opacity:1; transform: scale(1); } 50% { opacity:.55; transform: scale(.85); } }
          .gppl-title {
            font-family: "Playfair Display", "Times New Roman", serif;
            font-size: 40px; font-weight: 700; color: #fff;
            margin: 0 0 12px; line-height: 1.2;
          }
          .gppl-title-accent {
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            -webkit-background-clip: text; background-clip: text; color: transparent;
            font-style: italic; font-weight: 600;
          }
          .gppl-sub { font-size: 15px; color: #9aa0a8; max-width: 720px; margin: 0 auto 14px; line-height: 1.65; }
          .gppl-last-updated {
            display: inline-flex; align-items: center; gap: 8px;
            padding: 6px 14px;
            background: rgba(255,255,255,0.04);
            border: 1px dashed rgba(212,168,87,0.25);
            border-radius: 999px;
            font-size: 12.5px;
            color: #9aa0a8;
          }
          .gppl-last-updated strong { color: #d4a857; font-weight: 700; }
          .gppl-last-updated svg { color: #d4a857; }

          /* GRID */
          .gppl-grid {
            display: grid;
            grid-template-columns: 280px 1fr;
            gap: 28px;
            align-items: flex-start;
          }

          /* TOC */
          .gppl-toc-wrap {
            position: sticky;
            top: 100px;
            display: flex;
            flex-direction: column;
            gap: 16px;
          }
          .gppl-toc, .gppl-other {
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.2);
            border-radius: 16px;
            padding: 18px;
          }
          .gppl-toc-label {
            font-size: 10.5px;
            color: #d4a857;
            text-transform: uppercase;
            letter-spacing: 2px;
            font-weight: 700;
            padding-bottom: 10px;
            margin-bottom: 8px;
            border-bottom: 1px solid rgba(212,168,87,0.18);
          }
          .gppl-toc-list { display: flex; flex-direction: column; gap: 2px; max-height: 460px; overflow-y: auto; }
          .gppl-toc-list::-webkit-scrollbar { width: 4px; }
          .gppl-toc-list::-webkit-scrollbar-thumb { background: rgba(212,168,87,0.25); border-radius: 999px; }
          .gppl-toc-link {
            display: flex; align-items: center; gap: 10px;
            padding: 9px 10px;
            font-size: 12.5px;
            color: #9aa0a8;
            border-radius: 8px;
            text-decoration: none;
            line-height: 1.4;
            transition: color .2s ease, background .2s ease;
            cursor: pointer;
          }
          .gppl-toc-link:hover {
            color: #fff;
            background: rgba(212,168,87,0.06);
            text-decoration: none;
          }
          .gppl-toc-dot {
            width: 5px; height: 5px;
            border-radius: 50%;
            background: rgba(255,255,255,0.18);
            flex-shrink: 0;
            transition: background .2s ease, transform .2s ease;
          }
          .gppl-toc-link.is-active {
            color: #d4a857;
            background: rgba(212,168,87,0.1);
            font-weight: 600;
          }
          .gppl-toc-link.is-active .gppl-toc-dot {
            background: #d4a857;
            box-shadow: 0 0 8px #d4a857;
            transform: scale(1.3);
          }

          .gppl-other-list { display: flex; flex-direction: column; gap: 6px; }
          .gppl-other-link {
            display: flex; align-items: center; justify-content: space-between;
            padding: 11px 12px;
            background: rgba(255,255,255,0.02);
            border: 1px solid rgba(255,255,255,0.06);
            border-radius: 10px;
            text-decoration: none;
            color: inherit;
            transition: background .2s ease, border-color .2s ease, transform .2s ease;
          }
          .gppl-other-link:hover {
            background: rgba(212,168,87,0.06);
            border-color: rgba(212,168,87,0.32);
            text-decoration: none;
            transform: translateX(2px);
          }
          .gppl-other-link strong {
            display: block;
            color: #fff;
            font-size: 13px;
            font-weight: 700;
            margin-bottom: 2px;
            font-family: "Playfair Display", serif;
          }
          .gppl-other-link small { color: #9aa0a8; font-size: 11.5px; line-height: 1.4; }
          .gppl-other-arrow {
            color: #d4a857;
            font-size: 14px;
            font-weight: 700;
            transition: transform .2s ease;
          }
          .gppl-other-link:hover .gppl-other-arrow { transform: translateX(2px); }

          /* CONTENT */
          .gppl-content {
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.22);
            border-radius: 20px;
            padding: 36px 38px 30px;
            box-shadow: 0 12px 30px rgba(0,0,0,0.35);
          }
          .gppl-intro {
            padding-bottom: 22px;
            margin-bottom: 24px;
            border-bottom: 1px dashed rgba(212,168,87,0.2);
          }
          .gppl-intro p {
            font-size: 15px;
            line-height: 1.75;
            color: #c5c8cf;
            margin: 0 0 12px;
          }
          .gppl-intro p:last-child { margin-bottom: 0; }

          .gppl-notice {
            display: flex; align-items: flex-start; gap: 10px;
            padding: 14px 16px;
            background: rgba(212,168,87,0.08);
            border: 1px solid rgba(212,168,87,0.28);
            border-left: 3px solid #d4a857;
            border-radius: 10px;
            margin-bottom: 24px;
            font-size: 13.5px;
            color: #c5c8cf;
            line-height: 1.55;
          }
          .gppl-notice svg { color: #d4a857; flex-shrink: 0; margin-top: 1px; }

          .gppl-content-section {
            position: relative;
            padding: 24px 0 0;
            margin-bottom: 24px;
            border-top: 1px solid rgba(212,168,87,0.12);
            scroll-margin-top: 100px;
          }
          .gppl-content-section:first-of-type {
            border-top: none;
            padding-top: 0;
          }
          .gppl-section-num {
            font-family: "Playfair Display", serif;
            font-size: 13px;
            font-weight: 700;
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            -webkit-background-clip: text; background-clip: text; color: transparent;
            letter-spacing: 1px;
            margin-bottom: 4px;
          }
          .gppl-section-title {
            font-family: "Playfair Display", "Times New Roman", serif;
            font-size: 22px;
            color: #fff;
            font-weight: 700;
            margin: 0 0 14px;
            line-height: 1.3;
            position: relative;
            padding-left: 14px;
          }
          .gppl-section-title::before {
            content: "";
            position: absolute;
            left: 0; top: 6px; bottom: 6px;
            width: 3px;
            background: linear-gradient(180deg, #f1cf85, #b58a3f);
            border-radius: 999px;
          }

          .gppl-section-body p {
            font-size: 14.5px;
            line-height: 1.75;
            color: #b8bcc4;
            margin: 0 0 12px;
          }
          .gppl-section-body p:last-child { margin-bottom: 0; }
          .gppl-section-body b, .gppl-section-body strong { color: #fff; font-weight: 700; }
          .gppl-section-body a { color: #d4a857; text-decoration: none; font-weight: 600; }
          .gppl-section-body a:hover { color: #f1cf85; text-decoration: underline; }
          .gppl-section-body ul {
            list-style: none;
            padding: 0;
            margin: 8px 0 12px;
            display: flex; flex-direction: column; gap: 6px;
          }
          .gppl-section-body li {
            display: flex; align-items: flex-start; gap: 10px;
            font-size: 14px;
            color: #b8bcc4;
            line-height: 1.6;
            padding-left: 0;
          }
          .gppl-section-body li::before {
            content: "";
            width: 5px; height: 5px;
            border-radius: 50%;
            background: #d4a857;
            margin-top: 9px;
            flex-shrink: 0;
          }

          /* Contact card */
          .gppl-contact-card {
            position: relative;
            margin-top: 30px;
            padding: 28px;
            background: linear-gradient(135deg, rgba(212,168,87,0.1), rgba(212,168,87,0.02));
            border: 1px solid rgba(212,168,87,0.3);
            border-radius: 16px;
            text-align: center;
            overflow: hidden;
          }
          .gppl-contact-card::before {
            content: "";
            position: absolute;
            top: -80px; left: 50%;
            transform: translateX(-50%);
            width: 280px; height: 280px;
            background: radial-gradient(circle, rgba(212,168,87,0.15), transparent 70%);
            pointer-events: none;
          }
          .gppl-contact-icon {
            position: relative;
            width: 50px; height: 50px;
            margin: 0 auto 14px;
            background: rgba(212,168,87,0.16);
            border: 1px solid rgba(212,168,87,0.32);
            color: #d4a857;
            border-radius: 14px;
            display: flex; align-items: center; justify-content: center;
          }
          .gppl-contact-card h3 {
            position: relative;
            font-family: "Playfair Display", serif;
            font-size: 22px;
            color: #fff;
            font-weight: 700;
            margin: 0 0 8px;
          }
          .gppl-contact-card p {
            position: relative;
            font-size: 14px;
            color: #9aa0a8;
            line-height: 1.65;
            margin: 0 auto 20px;
            max-width: 460px;
          }
          .gppl-contact-actions {
            position: relative;
            display: inline-flex; flex-wrap: wrap; gap: 10px; justify-content: center;
            margin-bottom: 16px;
          }
          .gppl-btn {
            display: inline-flex; align-items: center; gap: 8px;
            padding: 11px 22px;
            border-radius: 10px;
            font-size: 12.5px;
            font-weight: 800;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            text-decoration: none;
            cursor: pointer;
            border: none;
            transition: transform .2s ease, box-shadow .25s ease, background .2s ease;
          }
          .gppl-btn-primary {
            background: linear-gradient(135deg, #f1cf85, #d4a857 50%, #b58a3f);
            color: #0b0b0c;
          }
          .gppl-btn-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 14px 30px rgba(212,168,87,0.35);
            color: #0b0b0c;
            text-decoration: none;
          }
          .gppl-btn-ghost {
            background: rgba(255,255,255,0.04);
            border: 1px solid rgba(212,168,87,0.3);
            color: #d4a857;
          }
          .gppl-btn-ghost:hover {
            background: rgba(212,168,87,0.12);
            border-color: rgba(212,168,87,0.55);
            color: #f1cf85;
            text-decoration: none;
            transform: translateY(-2px);
          }
          .gppl-contact-meta {
            position: relative;
            font-size: 11.5px;
            color: #6e7480;
            letter-spacing: 0.4px;
          }

          @media (max-width: 991px) {
            .gppl-grid { grid-template-columns: 1fr; gap: 18px; }
            .gppl-toc-wrap { position: static; flex-direction: row; gap: 12px; flex-wrap: wrap; }
            .gppl-toc, .gppl-other { flex: 1 1 280px; }
            .gppl-toc-list { max-height: 220px; }
            .gppl-content { padding: 28px 24px 24px; }
            .gppl-title { font-size: 32px; }
          }
          @media (max-width: 575px) {
            .gppl-section { padding: 40px 14px 70px; }
            .gppl-title { font-size: 26px; }
            .gppl-content { padding: 22px 18px; }
            .gppl-section-title { font-size: 18px; }
            .gppl-section-body p, .gppl-section-body li { font-size: 13.5px; }
            .gppl-toc-wrap { flex-direction: column; }
            .gppl-contact-card { padding: 24px 18px; }
            .gppl-contact-card h3 { font-size: 18px; }
          }
        `}</style>
      </section>

      <Footer />
      <Scrollbar />
    </Fragment>
  );
};

export default PolicyLayout;
